import { useMemo } from "react";
import type { JobItemDto } from "@artha/api-contracts";

type RecommendedJobSortSnapshot = {
  job: JobItemDto;
  payout: number;
  postedAt: number;
  title: string;
};

const getDisplayedPayoutValue = (job: JobItemDto): number => {
  const rawValue = job.cpa_value ?? job.cpc_value;
  return typeof rawValue === "number" && Number.isFinite(rawValue)
    ? rawValue
    : Number.NEGATIVE_INFINITY;
};

const buildRecommendedJobSortSnapshot = (
  job: JobItemDto
): RecommendedJobSortSnapshot => ({
  job,
  payout: getDisplayedPayoutValue(job),
  postedAt: Date.parse(job.posted_date || ""),
  title: job.title,
});

const compareRecommendedJobs = (
  firstJob: RecommendedJobSortSnapshot,
  secondJob: RecommendedJobSortSnapshot
): number => {
  if (firstJob.payout !== secondJob.payout) {
    return secondJob.payout - firstJob.payout;
  }

  const hasValidPostedDates =
    Number.isFinite(firstJob.postedAt) && Number.isFinite(secondJob.postedAt);

  if (hasValidPostedDates && firstJob.postedAt !== secondJob.postedAt) {
    return secondJob.postedAt - firstJob.postedAt;
  }

  return firstJob.title.localeCompare(secondJob.title);
};

export const useSortedRecommendedJobs = (
  jobs: JobItemDto[] | undefined
): JobItemDto[] =>
  useMemo(() => {
    const items = jobs ?? [];

    return items
      .map(buildRecommendedJobSortSnapshot)
      .sort(compareRecommendedJobs)
      .map(({ job }) => job);
  }, [jobs]);
